import type { UserRole } from "@/types";
import { documentCategoryLabels, type DocumentCategory } from "@/types";

// ============================================================
// Rollen-Modell 2026-10 — Single Source of Truth im Frontend
// (Backend-Pendant: api/src/Utils/Roles.php).
// ============================================================

/** de-DE-Labels der fünf Rollen. Vertrieb ist extern (eigene Domain). */
export const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Administrator",
  back_office: "Back Office",
  finanzen: "Finanzen",
  bauleiter: "Bauleiter",
  vertrieb: "Vertrieb (extern)",
};

/** Reihenfolge für Auswahllisten (intern zuerst, vertrieb zuletzt). */
export const ROLE_ORDER: UserRole[] = ["admin", "back_office", "finanzen", "bauleiter", "vertrieb"];

/** Badge-Farben (Tailwind-Klassen) je Rolle. */
const ROLE_BADGE_CLASSES: Record<UserRole, string> = {
  admin: "bg-purple-100 text-purple-800",
  back_office: "bg-blue-100 text-blue-800",
  finanzen: "bg-green-100 text-green-800",
  bauleiter: "bg-gray-100 text-gray-800",
  vertrieb: "bg-amber-100 text-amber-800",
};

/** Badge-Chip für eine Rolle (geteilte Darstellung in Sidebar/Admin/Profil). */
export function roleBadge(role: string): { label: string; className: string } {
  const key = (ROLE_LABELS[role as UserRole] ? role : "bauleiter") as UserRole;
  return {
    label: ROLE_LABELS[key],
    className: ROLE_BADGE_CLASSES[key],
  };
}

/** Büro-Rollen (globale Rechnungen + Dokumente-Schreibzugriff). */
export function isOfficeRole(role?: string): boolean {
  return role === "back_office" || role === "finanzen" || role === "admin";
}

/** Zahlungs-Rechte: Freigabe/bezahlt/Lexware — nur Finanzen + Admin. */
export function isFinancialRole(role?: string): boolean {
  return role === "finanzen" || role === "admin";
}

/** Klassifizierte Dokument-Kategorie (nur Finanzen/Admin). */
export const CLASSIFIED_DOC_CATEGORY: DocumentCategory = "geschaeftsintern";

/** Kategorien, die die UI für die Rolle zeigen darf (Backend-Gate bleibt). */
export function visibleDocumentCategories(role?: string): DocumentCategory[] {
  const cats = Object.keys(documentCategoryLabels) as DocumentCategory[];
  return isFinancialRole(role)
    ? cats
    : cats.filter((c) => c !== CLASSIFIED_DOC_CATEGORY);
}